/**
 * Sync Store - Zustand store for synchronization state management.
 *
 * Tracks sync status, online/offline state, conflict list, and queue status.
 * Provides actions for managing conflicts and sync lifecycle.
 *
 * @see Requirement 8.1
 */

import { create } from 'zustand';
import { Conflict } from '@/types';

// ─── Types ───────────────────────────────────────────────────────────────────

export type SyncStatusValue = 'synced' | 'syncing' | 'offline' | 'conflict';

export interface SyncState {
  /** Current sync status. */
  status: SyncStatusValue;
  /** ISO 8601 timestamp of the last successful sync. */
  lastSyncTime: string | null;
  /** List of unresolved conflicts. */
  conflicts: Conflict[];
  /** Number of items currently in the offline sync queue. */
  queueSize: number;
  /** Whether the device currently has network connectivity. */
  isOnline: boolean;
  /**
   * Force-flush the sync queue immediately. Registered by the active sync
   * queue (via {@link useProductivitySync}) and `null` when no queue is
   * mounted (e.g. signed out).
   */
  flushNow: (() => Promise<void>) | null;
}

export interface SyncActions {
  /** Update the current sync status. */
  setSyncStatus: (status: SyncStatusValue) => void;
  /** Add a new conflict to the list. */
  addConflict: (conflict: Conflict) => void;
  /** Resolve and remove a conflict by its ID. */
  resolveConflict: (conflictId: string) => void;
  /** Set the online/offline status. */
  setOnlineStatus: (isOnline: boolean) => void;
  /** Update the last sync timestamp. */
  setLastSyncTime: (time: string) => void;
  /** Update the queue size. */
  setQueueSize: (size: number) => void;
  /** Set the full list of conflicts (e.g., after fetching from server). */
  setConflicts: (conflicts: Conflict[]) => void;
  /**
   * Register (or clear with `null`) the function used to force-flush the sync
   * queue. Called by the sync queue lifecycle on mount/cleanup.
   */
  setFlushFn: (fn: (() => Promise<void>) | null) => void;
}

export type SyncStore = SyncState & SyncActions;

// ─── Store ───────────────────────────────────────────────────────────────────

export const useSyncStore = create<SyncStore>((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────────────
  status: 'synced',
  lastSyncTime: null,
  conflicts: [],
  queueSize: 0,
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  flushNow: null,

  // ── Actions ────────────────────────────────────────────────────────────────
  setSyncStatus: (status) => {
    set({ status });
  },

  addConflict: (conflict) => {
    set({ conflicts: [...get().conflicts, conflict], status: 'conflict' });
  },

  resolveConflict: (conflictId) => {
    const remaining = get().conflicts.filter((c) => c.conflictId !== conflictId);
    set({
      conflicts: remaining,
      // If no more conflicts, revert to synced (unless syncing)
      status: remaining.length === 0 ? 'synced' : 'conflict',
    });
  },

  setOnlineStatus: (isOnline) => {
    set({
      isOnline,
      // When going offline, reflect it in status (unless there are conflicts)
      status: !isOnline
        ? 'offline'
        : get().conflicts.length > 0
          ? 'conflict'
          : 'synced',
    });
  },

  setLastSyncTime: (time) => {
    set({ lastSyncTime: time });
  },

  setQueueSize: (size) => {
    set({ queueSize: size });
  },

  setConflicts: (conflicts) => {
    set({
      conflicts,
      status: conflicts.length > 0 ? 'conflict' : get().status,
    });
  },

  setFlushFn: (fn) => {
    set({ flushNow: fn });
  },
}));
